import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

function occupancyPercent(count: number, capacity: number | null): number | null {
  if (!capacity) return null
  return Math.min(100, Math.round((count / capacity) * 100))
}

function toneFor(pct: number): string {
  if (pct >= 100) return 'bg-danger-500'
  if (pct >= 85) return 'bg-warning-500'
  return 'bg-success-500'
}

interface OccupancyBarProps {
  count: number
  capacity: number | null
  /** Accessible name, e.g. "Section A occupancy". */
  label: string
  className?: string
}

/** Thin seats-filled meter; renders a neutral track when the capacity is unlimited. */
export function OccupancyBar({ count, capacity, label, className }: OccupancyBarProps) {
  const { t } = useTranslation('academic')
  const pct = occupancyPercent(count, capacity)
  return (
    <div
      className={cn('h-1.5 overflow-hidden rounded-full bg-surface-sunken', className)}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={capacity ?? undefined}
      aria-valuenow={count}
      aria-valuetext={
        capacity
          ? t('{{filled}} of {{capacity}} seats filled', { filled: count, capacity })
          : t('{{count}} students, no capacity limit', { count })
      }
    >
      {pct !== null ? (
        <div
          className={cn('h-full rounded-full transition-[width]', toneFor(pct))}
          style={{ width: `${pct}%` }}
        />
      ) : null}
    </div>
  )
}

/** "12/30" with the bar underneath — used in tables and cards. */
export function Occupancy({ count, capacity, label, className }: OccupancyBarProps) {
  const { t } = useTranslation('academic')
  return (
    <div className={cn('min-w-24', className)}>
      <p className="text-xs text-ink-soft tabular">
        <span className="font-medium text-ink">{count}</span>
        {capacity ? (
          <span className="text-muted"> / {capacity}</span>
        ) : (
          <span className="text-muted"> · {t('no limit')}</span>
        )}
      </p>
      <OccupancyBar count={count} capacity={capacity} label={label} className="mt-1" />
    </div>
  )
}
